/**
 * src/shell/agents/components/BattleCardMatrix.tsx
 *
 * Interactive 2x2 Competitive Quadrant Matrix & Objection Handling Accordion:
 * - Visual 2x2 Quadrant Positioning (Decentralized vs Centralized, Simple vs Complex)
 * - "Where We Win" vs "Where They Win" Side-by-Side Grid
 * - Sales Talk Tracks & Objection Handling Accordion
 * - Link to WebRTC Voice AI Coach
 */

import React, { useState } from 'react';
import { BattleCardResult } from '../types/gstack-agents-types';
import { agentExportPipeline } from '../services/AgentExportPipeline';

interface BattleCardMatrixProps {
  result: BattleCardResult;
  onLaunchVoiceCoach?: (script: string) => void;
}

export const BattleCardMatrix: React.FC<BattleCardMatrixProps> = ({
  result,
  onLaunchVoiceCoach,
}) => {
  const [selectedCompIdx, setSelectedCompIdx] = useState(0);
  const [expandedObjectionIdx, setExpandedObjectionIdx] = useState<number | null>(0);
  const [exportFeedback, setExportFeedback] = useState<string | null>(null);

  const currentCard = result.cards[selectedCompIdx] || result.cards[0];
  if (!currentCard) {
    return <div style={{ color: '#94a3b8', padding: 24 }}>No battle cards generated.</div>;
  }

  const handleExport = (format: 'markdown' | 'pdf') => {
    const markdown = `# Battle Card: Sovereign Mesh vs ${currentCard.competitor.name}

## Summary
${currentCard.summary}

## Positioning
- Target Customers: ${currentCard.positioning.targetCustomers}
- Value Prop: ${currentCard.positioning.valueProp}
- Pricing Model: ${currentCard.positioning.pricingModel || 'N/A'}

## Where We Win
${currentCard.differentiation.whereWeWin.map((w) => `- ${w}`).join('\n')}

## Where They Win
${currentCard.differentiation.whereTheyWin.map((w) => `- ${w}`).join('\n')}

## Opening Pitch
${currentCard.talkTracks.openingPitch}

## Objection Handling
${currentCard.talkTracks.objectionResponses
  .map(
    (o) =>
      `### Objection: ${o.objection}\n- **Response**: ${o.recommendedResponse}\n- **Proof**: ${o.proofPoint}\n`
  )
  .join('\n')}`;

    const { mimeType, payload, downloadFilename } = agentExportPipeline.exportContent(
      markdown,
      format,
      `battle-card-${currentCard.competitor.name.toLowerCase().replace(/\s+/g, '-')}`
    );
    agentExportPipeline.triggerClientDownload(payload, mimeType, downloadFilename);
    setExportFeedback(`Exported ${downloadFilename}`);
    setTimeout(() => setExportFeedback(null), 3000);
  };

  return (
    <div
      data-testid="battle-card-matrix-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        background: '#0b0f19',
        borderRadius: 12,
        padding: 24,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        color: '#f8fafc',
      }}
    >
      {/* Competitor Selector & Export Controls */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          {result.cards.map((c, idx) => (
            <button
              key={c.competitor.name}
              type="button"
              data-testid={`btn-competitor-tab-${idx}`}
              onClick={() => setSelectedCompIdx(idx)}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                background: selectedCompIdx === idx ? '#e11d48' : 'rgba(255, 255, 255, 0.06)',
                color: selectedCompIdx === idx ? '#fff' : '#94a3b8',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              🆚 vs. {c.competitor.name}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            data-testid="btn-launch-voice-coach"
            onClick={() => onLaunchVoiceCoach?.(currentCard.voiceCoachScript)}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            🎙️ Practice with Voice Coach
          </button>
          <button
            type="button"
            data-testid="btn-export-battlecard-md"
            onClick={() => handleExport('markdown')}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              fontSize: 12,
              border: '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer',
            }}
          >
            📥 Export Markdown
          </button>
        </div>
      </div>

      {exportFeedback && (
        <div style={{ padding: '6px 18px', background: '#065f46', color: '#6ee7b7', fontSize: 12 }}>
          ✓ {exportFeedback}
        </div>
      )}

      {/* 2x2 Competitive Quadrant & Differentiation Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 20,
        }}
      >
        {/* 2x2 Quadrant Visualizer */}
        <div
          data-testid="quadrant-visualizer"
          style={{
            background: 'rgba(15, 23, 42, 0.8)',
            padding: 20,
            borderRadius: 8,
            border: '1px solid rgba(255, 255, 255, 0.08)',
            position: 'relative',
            minHeight: 260,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ textAlign: 'center', fontSize: 11, color: '#38bdf8', fontWeight: 700 }}>
            ▲ Decentralized & Sovereign
          </div>

          <div
            style={{
              position: 'relative',
              height: 180,
              margin: '10px 0',
              border: '1px dashed rgba(255,255,255,0.15)',
              borderRadius: 6,
            }}
          >
            {/* Center Axes */}
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.1)' }} />
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.1)' }} />

            {/* Us: Top Right */}
            <div
              data-testid="quadrant-node-us"
              style={{
                position: 'absolute',
                top: '20%',
                right: '20%',
                background: '#10b981',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 700,
                boxShadow: '0 0 12px rgba(16, 185, 129, 0.5)',
              }}
            >
              🏆 Sovereign Mesh
            </div>

            {/* Competitor: Bottom Left */}
            <div
              data-testid="quadrant-node-competitor"
              style={{
                position: 'absolute',
                bottom: '25%',
                left: '20%',
                background: '#ef4444',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {currentCard.competitor.name}
            </div>
          </div>

          <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
            ▼ Centralized Cloud SaaS
          </div>
        </div>

        {/* Differentiation Cards: Where We Win vs Where They Win */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            data-testid="card-where-we-win"
            style={{
              background: 'rgba(6, 78, 59, 0.4)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4ade80', marginBottom: 8 }}>
              🟢 Where We Win (Key Differentiators)
            </h4>
            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
              {currentCard.differentiation.whereWeWin.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>

          <div
            data-testid="card-where-they-win"
            style={{
              background: 'rgba(127, 29, 29, 0.3)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#f87171', marginBottom: 8 }}>
              🔴 Where They Win (Acknowledge & Pivot)
            </h4>
            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, color: '#cbd5e1', lineHeight: 1.5 }}>
              {currentCard.differentiation.whereTheyWin.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Objection Handling Scripts */}
      <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: 18, borderRadius: 8 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#f1f5f9' }}>
          🛡️ Objection Handling & Live Talk Tracks
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {currentCard.talkTracks.objectionResponses.map((obj, idx) => (
            <div
              key={idx}
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: 6,
                border: '1px solid rgba(255, 255, 255, 0.06)',
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                data-testid={`btn-objection-${idx}`}
                onClick={() => setExpandedObjectionIdx(expandedObjectionIdx === idx ? null : idx)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span>{obj.objection}</span>
                <span>{expandedObjectionIdx === idx ? '▲' : '▼'}</span>
              </button>

              {expandedObjectionIdx === idx && (
                <div style={{ padding: '0 16px 14px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 13, color: '#38bdf8', marginTop: 10, lineHeight: 1.5 }}>
                    <strong>Recommended Response:</strong> {obj.recommendedResponse}
                  </div>
                  <div style={{ fontSize: 11, color: '#4ade80', marginTop: 6 }}>
                    <strong>Proof Point:</strong> {obj.proofPoint}
                  </div>
                  {obj.avoidSaying && (
                    <div style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>
                      <strong>Avoid:</strong> {obj.avoidSaying}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
